/*
 * section-analytics.js
 * ---------------------------------------------------------------------------
 * Sends section-level engagement events straight to GA4 (G-7B9JJ7FREH) via
 * the Google tag that Shopify's Google & YouTube channel loads after cookie
 * consent. No GTM. See the contract doc:
 *   docs/section-analytics-datalayer-spec.md
 *
 * Two events:
 *   section_view       — a section was genuinely seen (>=50% visible for ~1s),
 *                        pushed at most once per section per page load.
 *   section_cta_click  — a link / button / [role=button] inside a section was
 *                        clicked.
 *
 * Coverage: EVERY <div class="shopify-section"> on the page is tracked
 * automatically — new sections you add later need zero JS changes. To give a
 * section a readable name in reports, put data-track-section="Friendly Name"
 * on its root element (for reused sections, bind it to a setting, e.g.
 *   data-track-section="{{ section.settings.heading | default: 'Featured' | escape }}"
 * so each instance reports distinctly). To exclude a section, add
 * data-track-ignore to its root.
 *
 * PRIVACY: emits NO customer data — no customer object, cookies, email, name,
 * or IP is ever read. It captures only section identity, the URL path (no query
 * string), and the label/destination of clicked CTAs. It does not run at all on
 * account / auth / checkout / order pages, hrefs are reduced to path-only, and
 * captured labels are scrubbed of email/long-number patterns. Events go to GA4
 * ONLY (send_to pins them) — never to ad pixels (Google Ads/Meta/TikTok) — and
 * nothing is sent until the visitor has allowed analytics.
 * ---------------------------------------------------------------------------
 */
(function () {
  'use strict';

  if (window.__sectionAnalyticsLoaded) return;
  window.__sectionAnalyticsLoaded = true;

  // ---- transport -----------------------------------------------------------
  // Events go straight to GA4 through the Google tag that the Shopify
  // "Google & YouTube" channel pixel puts on the page. That tag only exists once
  // the visitor has allowed analytics in the cookie banner, so consent gating is
  // built in, and we load no extra library (no GTM).
  //   - send_to pins every event to GA4. Without it gtag would fan the event
  //     out to every configured Google destination (Google Ads, Merchant Center).
  //   - Events fired before the tag is ready (or before consent) wait in a small
  //     in-memory queue and are sent once it is. Without consent they are never
  //     sent and are discarded when the page unloads.
  var GA4_ID = 'G-7B9JJ7FREH';
  var MAX_QUEUE = 300;
  var queue = [];
  var pollTimer = null;

  // True once GA4 itself has booted on the page. (A bare window.gtag check is
  // not enough: other scripts, e.g. PageFly, can define a stub gtag.)
  function ga4Ready() {
    return typeof window.gtag === 'function' &&
      !!(window.google_tag_manager && window.google_tag_manager[GA4_ID]);
  }

  function flush() {
    while (queue.length) {
      var q = queue.shift();
      window.gtag('event', q[0], q[1]);
    }
  }

  // Poll briefly for the tag: it appears a few seconds after load for visitors
  // who consented earlier, or shortly after they accept the banner.
  function waitForGa4(maxMs) {
    if (pollTimer) return;
    var waited = 0;
    pollTimer = setInterval(function () {
      waited += 500;
      if (ga4Ready()) {
        clearInterval(pollTimer); pollTimer = null;
        flush();
      } else if (waited >= maxMs) {
        clearInterval(pollTimer); pollTimer = null;
      }
    }, 500);
  }

  // Visitor accepts the banner on this page: (re)start waiting for the tag.
  document.addEventListener('visitorConsentCollected', function (e) {
    if (!(e.detail && e.detail.analyticsAllowed) || !queue.length) return;
    if (pollTimer) { clearInterval(pollTimer); pollTimer = null; }
    waitForGa4(20000);
  });

  function send(name, params) {
    var p = { send_to: GA4_ID };
    for (var k in params) {
      var v = params[k];
      if (v !== undefined && v !== null && v !== '') p[k] = v;
    }
    if (ga4Ready()) {
      flush();
      window.gtag('event', name, p);
      return;
    }
    if (queue.length < MAX_QUEUE) queue.push([name, p]);
    waitForGa4(30000);
  }

  // Shared with other first-party trackers (e.g. the product quiz).
  window.GNAnalytics = window.GNAnalytics || {};
  window.GNAnalytics.send = send;
  var early = window.__gnAnalyticsQueue;
  if (early && early.length) {
    for (var qi = 0; qi < early.length; qi++) send(early[qi][0], early[qi][1]);
    early.length = 0;
  }

  function pushEvent(payload) {
    var params = {};
    for (var k in payload) if (k !== 'event') params[k] = payload[k];
    send(payload.event, params);
  }

  // How long a section must stay >=50% visible before it counts as "viewed".
  var DWELL_MS = 1000;
  var VIEW_RATIO = 0.5;

  // Structural / functional sections that aren't content a customer engages
  // with — matched as a substring of the resolved section key. Keeps the
  // "track everything" default from drowning the reports in noise.
  var IGNORE_KEYS = [
    'maincontent',
    'cart-live-region',
    'cart-icon-bubble',
    'cart-notification',
    'predictive-search',
    'pickup-availability',
    'transition',
    'apps'
  ];

  var pageType = (function () {
    var m = (document.body.className || '').match(/template-([a-z0-9_-]+)/i);
    return m ? m[1] : 'unknown';
  })();

  // PRIVACY: never track on account / auth / checkout / order pages. These can
  // render customer names, emails, and order ids in the DOM, so we opt out
  // entirely rather than risk capturing PII. Marketing
  // analytics doesn't need these pages anyway.
  var SENSITIVE_PAGE_TYPE = /customer|checkout|gift_card|password/i;
  var SENSITIVE_PATH = /^\/(account|checkout|challenge)(\/|$)/i;
  if (SENSITIVE_PAGE_TYPE.test(pageType) || SENSITIVE_PATH.test(location.pathname)) {
    return;
  }

  // Reduce a link href to a PATH only. Never carry query strings or fragments
  // (tokens, emails, discount codes live there), and never carry the address of
  // a mailto:/tel: link — only its scheme.
  function sanitizeDestination(href) {
    if (!href) return '';
    var s = String(href).trim();
    if (/^(mailto|tel):/i.test(s)) return s.split(':')[0].toLowerCase();
    if (/^(#|javascript:)/i.test(s)) return '';
    try {
      var u = new URL(s, location.origin);
      return u.origin === location.origin ? u.pathname : u.host + u.pathname;
    } catch (e) {
      return s.split('?')[0].split('#')[0];
    }
  }

  // Strip anything that looks like PII out of a captured label (belt-and-braces,
  // since account pages are already excluded above).
  function redact(text) {
    return text
      .replace(/[^\s@]+@[^\s@]+\.[^\s@]+/g, '[redacted]') // emails
      .replace(/\d{7,}/g, '[redacted]');                  // long digit runs (phone/order)
  }

  // Last-resort label for text-less CTAs (e.g. product-card image links):
  // turn the destination's final path segment into a readable name.
  // "/products/cocomint-pulling-oil" -> "Cocomint Pulling Oil".
  function humanizeHref(dest) {
    if (!dest) return '';
    var seg = String(dest).split('?')[0].split('#')[0].replace(/\/+$/, '');
    seg = seg.substring(seg.lastIndexOf('/') + 1).replace(/[-_]+/g, ' ').trim();
    return seg.replace(/\b\w/g, function (c) { return c.toUpperCase(); });
  }

  // Turn a raw section id ("shopify-section-template--123__brand_hero_cBRTUX")
  // into a stable, readable key ("brand-hero").
  function cleanKey(raw) {
    if (!raw) return '';
    return String(raw)
      .replace(/^shopify-section-/, '')
      .replace(/^(template|sections)--\d+__/, '')
      .replace(/_[A-Za-z0-9]{6}$/, '') // strip the random suffix Shopify appends
      .replace(/[_\s]+/g, '-')
      .toLowerCase();
  }

  function attrWithin(sectionEl, name) {
    if (sectionEl.hasAttribute(name)) return sectionEl.getAttribute(name);
    var child = sectionEl.querySelector('[' + name + ']');
    return child ? child.getAttribute(name) : null;
  }

  // Resolve the identity of a .shopify-section element.
  function resolve(sectionEl) {
    var key = cleanKey(sectionEl.id) || (sectionEl.id || '').toLowerCase();
    var friendly = (attrWithin(sectionEl, 'data-track-section') || '').trim();
    var type = (attrWithin(sectionEl, 'data-track-section-type') || '').trim();
    return {
      section_id: sectionEl.id || key,
      section_key: key,
      section_type: type || key,
      section_name: friendly || key || 'unknown'
    };
  }

  function isIgnored(sectionEl, id) {
    if (sectionEl.hasAttribute('data-track-ignore')) return true;
    var key = id.section_key || '';
    for (var i = 0; i < IGNORE_KEYS.length; i++) {
      if (key.indexOf(IGNORE_KEYS[i]) !== -1) return true;
    }
    return false;
  }

  // Cached DOM-order list of sections, for stable position numbers.
  var sections = [];
  function indexSections() {
    sections = Array.prototype.slice.call(
      document.querySelectorAll('.shopify-section')
    );
  }
  function positionOf(el) {
    var i = sections.indexOf(el);
    return i === -1 ? 0 : i + 1;
  }

  function basePayload(sectionEl, id) {
    return {
      section_id: id.section_id,
      section_key: id.section_key,
      section_type: id.section_type,
      section_name: id.section_name,
      section_position: positionOf(sectionEl),
      page_type: pageType,
      page_path: location.pathname
    };
  }

  // ---- item-level (sub-section) tracking ---------------------------------
  // Elements marked data-track-item inside a section — carousel slides,
  // product cards, routine steps, etc. Views are emitted by the section's own
  // JS (which knows when a slide activates); clicks are auto-enriched below.
  function itemFields(itemEl) {
    var dest = sanitizeDestination(itemEl.getAttribute('data-track-item-destination'));
    return {
      item_type: itemEl.getAttribute('data-track-item') || 'item',
      item_name: (itemEl.getAttribute('data-track-item-name') || '').trim(),
      item_position: itemEl.getAttribute('data-track-item-position') || '',
      item_destination: dest
    };
  }

  // Public API so a section's own script can log an item view with the correct
  // section context, e.g. window.SectionAnalytics.itemView(slideEl).
  function pushItemView(itemEl) {
    if (!itemEl || !itemEl.closest) return;
    var sectionEl = itemEl.closest('.shopify-section');
    if (!sectionEl) return;
    var id = resolve(sectionEl);
    if (isIgnored(sectionEl, id)) return;
    var f = itemFields(itemEl);
    var payload = basePayload(sectionEl, id);
    payload.event = 'section_item_view';
    payload.item_type = f.item_type;
    payload.item_name = f.item_name || humanizeHref(f.item_destination) || f.item_type;
    payload.item_position = f.item_position;
    payload.item_destination = f.item_destination;
    pushEvent(payload);
  }

  window.SectionAnalytics = window.SectionAnalytics || {};
  window.SectionAnalytics.itemView = pushItemView;

  // ---- section_view ------------------------------------------------------
  var io = null;
  var seen = new WeakMap();
  var timers = new WeakMap();

  function pushView(sectionEl) {
    var id = resolve(sectionEl);
    var payload = basePayload(sectionEl, id);
    payload.event = 'section_view';
    pushEvent(payload);
  }

  function onIntersect(entries) {
    entries.forEach(function (entry) {
      var el = entry.target;
      var visible = entry.isIntersecting && entry.intersectionRatio >= VIEW_RATIO;
      if (visible) {
        if (seen.get(el) || timers.get(el)) return;
        var t = setTimeout(function () {
          timers['delete'](el);
          if (seen.get(el)) return;
          seen.set(el, true);
          if (io) io.unobserve(el);
          pushView(el);
        }, DWELL_MS);
        timers.set(el, t);
      } else {
        var pending = timers.get(el);
        if (pending) {
          clearTimeout(pending);
          timers['delete'](el);
        }
      }
    });
  }

  function observeSection(el) {
    var id = resolve(el);
    if (isIgnored(el, id)) return;
    if (io) io.observe(el);
  }

  function startViews() {
    indexSections();
    if (!('IntersectionObserver' in window)) return;
    if (!io) {
      io = new IntersectionObserver(onIntersect, {
        threshold: [0, VIEW_RATIO, 1]
      });
    }
    sections.forEach(observeSection);
  }

  // ---- section_cta_click -------------------------------------------------
  // Capture phase so clicks whose handlers call stopPropagation (add-to-cart,
  // quick-view, etc.) are still recorded.
  document.addEventListener('click', function (e) {
    var target = e.target;
    if (!target || !target.closest) return;
    var cta = target.closest('a, button, [role="button"]');
    if (!cta) return;
    var sectionEl = cta.closest('.shopify-section');
    if (!sectionEl) return;

    var id = resolve(sectionEl);
    if (isIgnored(sectionEl, id)) return;
    // Element-level opt-out (e.g. a video play overlay tracked via video events).
    if (cta.closest('[data-track-ignore]')) return;

    var href = cta.tagName === 'A' ? sanitizeDestination(cta.getAttribute('href')) : '';

    // Prefer real text; fall back through title, a nested image's alt, and
    // finally a name derived from the destination — so image-only links (like
    // product cards) still report which product was clicked.
    var img = cta.querySelector ? cta.querySelector('img[alt]') : null;
    var label = (
      cta.getAttribute('data-track-cta') ||
      cta.getAttribute('aria-label') ||
      (cta.textContent || '').replace(/\s+/g, ' ').trim() ||
      cta.getAttribute('title') ||
      (img && img.getAttribute('alt')) ||
      humanizeHref(href) ||
      ''
    );
    label = redact(label.replace(/\s+/g, ' ').trim()).slice(0, 120) || '(no label)';

    var payload = basePayload(sectionEl, id);
    payload.event = 'section_cta_click';
    payload.cta_label = label || '(no label)';
    payload.cta_destination = href;
    payload.cta_tag = cta.tagName.toLowerCase();

    // If the click happened inside a tracked sub-item (e.g. a carousel slide),
    // attach which item — so "clicks per slide" is a first-class breakdown.
    var itemEl = cta.closest('[data-track-item]');
    if (itemEl && sectionEl.contains(itemEl)) {
      var f = itemFields(itemEl);
      payload.item_type = f.item_type;
      payload.item_name = f.item_name || humanizeHref(f.item_destination) || label;
      payload.item_position = f.item_position;
      payload.item_destination = f.item_destination;
    }
    pushEvent(payload);
  }, true);

  // ---- scroll depth (opt-in via data-track-scroll) -----------------------
  // For horizontally-scrolling carousels (and vertical scrollers): emits a
  // section_scroll event as the user passes 25/50/75/100% of the track, each
  // once per page load. Answers "do people scroll past what's initially shown,
  // and how far".
  function attachScroll(el) {
    if (el.__scrollTracked) return;
    el.__scrollTracked = true;
    var sectionEl = el.closest('.shopify-section');
    if (!sectionEl) return;
    var id = resolve(sectionEl);
    if (isIgnored(sectionEl, id)) return;

    var axis = (el.getAttribute('data-track-scroll-axis') || 'horizontal').toLowerCase();
    var label = el.getAttribute('data-track-scroll') || 'scroll';
    // Optional sub-context, e.g. which tab this scroller belongs to.
    var context = (el.getAttribute('data-track-scroll-context') || '').trim();
    var milestones = [25, 50, 75, 100];
    var hit = {};
    var ticking = false;

    function measure() {
      ticking = false;
      var max = axis === 'vertical'
        ? el.scrollHeight - el.clientHeight
        : el.scrollWidth - el.clientWidth;
      if (max <= 4) return; // nothing to scroll
      var pos = axis === 'vertical' ? el.scrollTop : el.scrollLeft;
      var depth = Math.min(100, Math.round((pos / max) * 100));
      for (var k = 0; k < milestones.length; k++) {
        var m = milestones[k];
        if (depth >= m && !hit[m]) {
          hit[m] = true;
          var payload = basePayload(sectionEl, id);
          payload.event = 'section_scroll';
          payload.scroll_label = label;
          payload.scroll_axis = axis;
          payload.scroll_depth = m;
          payload.scroll_items = el.children ? el.children.length : 0;
          if (context) payload.scroll_context = context;
          pushEvent(payload);
        }
      }
    }

    el.addEventListener('scroll', function () {
      if (!ticking) { ticking = true; requestAnimationFrame(measure); }
    }, { passive: true });
  }

  function initScrollTracking() {
    var els = document.querySelectorAll('[data-track-scroll]');
    Array.prototype.forEach.call(els, attachScroll);
  }

  // ---- video engagement (opt-in via data-track-video) --------------------
  // For self-hosted <video> inside a [data-track-video] wrapper. Emits:
  //   section_video_play      — first play (the "play button" metric)
  //   section_video_progress  — as playback passes 25/50/75/100% of duration
  //   section_video_watch     — accumulated watch time, flushed when the user
  //                             leaves/hides the page or the clip ends
  // Video identity comes from the enclosing [data-track-item] (name/position).
  function attachVideo(video) {
    if (video.__videoTracked) return;
    video.__videoTracked = true;
    var sectionEl = video.closest('.shopify-section');
    if (!sectionEl) return;
    var id = resolve(sectionEl);
    if (isIgnored(sectionEl, id)) return;

    var itemEl = video.closest('[data-track-item]');
    var f = itemEl ? itemFields(itemEl) : null;
    var name = (video.getAttribute('data-track-video-name') || (f && f.item_name) || '').trim();
    var pos = video.getAttribute('data-track-video-position') || (f && f.item_position) || '';

    var watched = 0, lastT = 0, maxPct = 0, playing = false, started = false, reported = false;
    var mHit = {};

    function base() {
      var p = basePayload(sectionEl, id);
      p.item_type = 'video';
      p.item_name = name || ('Video ' + pos);
      p.item_position = pos;
      return p;
    }

    video.addEventListener('play', function () {
      playing = true; lastT = video.currentTime || 0;
      if (!started) {
        started = true;
        var p = base(); p.event = 'section_video_play';
        p.video_duration = Math.round(video.duration || 0);
        pushEvent(p);
      }
    });

    video.addEventListener('timeupdate', function () {
      if (!playing) return;
      var d = video.currentTime - lastT;
      lastT = video.currentTime;
      if (d > 0 && d < 2) watched += d; // ignore seeks / tab-throttle jumps
      if (video.duration) {
        var pct = (video.currentTime / video.duration) * 100;
        if (pct > maxPct) maxPct = pct;
        for (var k = 0; k < 4; k++) {
          var m = [25, 50, 75, 100][k];
          if (pct >= m && !mHit[m]) {
            mHit[m] = true;
            var p = base(); p.event = 'section_video_progress'; p.video_percent = m;
            pushEvent(p);
          }
        }
      }
    });

    video.addEventListener('pause', function () { playing = false; });
    video.addEventListener('ended', function () { playing = false; flush('ended'); });

    function flush(reason) {
      if (reported || watched < 1) return;
      reported = true;
      var p = base();
      p.event = 'section_video_watch';
      p.watch_seconds = Math.round(watched);
      p.watch_percent = Math.min(100, Math.round(maxPct));
      p.video_duration = Math.round(video.duration || 0);
      p.watch_reason = reason;
      pushEvent(p);
    }

    // Flush accumulated watch time when the user hides or leaves the page.
    document.addEventListener('visibilitychange', function () {
      if (document.visibilityState === 'hidden') { playing = false; flush('hidden'); }
    });
    window.addEventListener('pagehide', function () { flush('pagehide'); });
  }

  function initVideoTracking() {
    var vids = document.querySelectorAll('[data-track-video] video, video[data-track-video]');
    Array.prototype.forEach.call(vids, attachVideo);
  }

  // ---- init + theme-editor support --------------------------------------
  function init() {
    startViews();
    initScrollTracking();
    initVideoTracking();
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }

  // Re-observe sections re-rendered in the theme editor.
  document.addEventListener('shopify:section:load', function (e) {
    indexSections();
    var el = e.target;
    if (el && el.classList && el.classList.contains('shopify-section')) {
      seen['delete'](el);
      observeSection(el);
    }
    initScrollTracking();
    initVideoTracking();
  });
})();
